/* Fovré theme — scroll reveals + lightweight cart plumbing. No dependencies. */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------------
   * Scroll reveals — one-shot IntersectionObserver on [.fv-reveal] elements.
   * Re-scans on shopify:section:load so re-added sections animate in the editor.
   * ------------------------------------------------------------------------- */
  var revealObserver = null;

  function ensureObserver() {
    if (revealObserver || prefersReducedMotion || !('IntersectionObserver' in window)) return;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('fv-in');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -5% 0px' }
    );
  }

  function observeReveals(root) {
    var scope = root || document;
    var els = scope.querySelectorAll('.fv-reveal:not(.fv-in)');
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      // No animation: make everything visible immediately.
      els.forEach(function (el) { el.classList.add('fv-in'); });
      return;
    }
    ensureObserver();
    els.forEach(function (el) { revealObserver.observe(el); });
  }

  /* ---------------------------------------------------------------------------
   * Hero video — plays only when motion is welcome.
   * The markup deliberately ships without an `autoplay` attribute: that keeps
   * preload="metadata" in force, so a hero we decide not to play never
   * downloads the video body at all. Suppressing autoplay from CSS is not
   * possible, which is why this lives in JS.
   * ------------------------------------------------------------------------- */
  var MOBILE_QUERY = '(max-width: 749px)';

  function initHeroVideo(root) {
    var scope = root || document;
    var figures = scope.querySelectorAll('.fv-hero__figure[data-video-mobile]');

    Array.prototype.forEach.call(figures, function (figure) {
      var video = figure.querySelector('video');
      if (!video) return;

      if (prefersReducedMotion) return;

      var isMobile = window.matchMedia(MOBILE_QUERY).matches;
      if (isMobile && figure.getAttribute('data-video-mobile') !== 'true') return;

      var started = video.play();
      // Browsers reject this when their autoplay policy blocks playback. The
      // poster stays up, which is an acceptable outcome — swallow it rather
      // than emitting an unhandled rejection.
      if (started && typeof started.catch === 'function') {
        started.catch(function () {});
      }
    });
  }

  /* ---------------------------------------------------------------------------
   * Featured product video — plays while its section is on screen, pauses once
   * it scrolls away. Unlike the hero gate above, this needs an observer that
   * keeps watching rather than firing once, so it can pause on scroll-out.
   * ------------------------------------------------------------------------- */
  var fpVideoObserver = null;

  function initFeaturedProductVideo(root) {
    var scope = root || document;
    // The night chapter, film chapters, and product stage share the same
    // on-screen gate as the product video.
    var videos = scope.querySelectorAll('.fv-fp__main video, .fv-night__figure video, .fv-film__figure video, .fv-stage__panel video');
    if (!videos.length || prefersReducedMotion || !('IntersectionObserver' in window)) return;

    if (!fpVideoObserver) {
      fpVideoObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              var started = entry.target.play();
              if (started && typeof started.catch === 'function') {
                started.catch(function () {});
              }
            } else {
              entry.target.pause();
            }
          });
        },
        { threshold: 0.5 }
      );
    }
    videos.forEach(function (video) { fpVideoObserver.observe(video); });
  }

  /* ---------------------------------------------------------------------------
   * Cart count — updates every [data-cart-count] on the page from cart state.
   * ------------------------------------------------------------------------- */
  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
    });
  }

  function refreshCartCount() {
    fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js', {
      headers: { 'Accept': 'application/json' },
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCartCount(cart.item_count); })
      .catch(function () {});
  }

  /* ---------------------------------------------------------------------------
   * <product-form> — AJAX add-to-cart. Wraps a standard product <form>.
   * Attributes: data-cart-type="notification|page".
   * ------------------------------------------------------------------------- */
  if ('customElements' in window && !customElements.get('product-form')) {
    customElements.define(
      'product-form',
      class ProductForm extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          if (!this.form) return;
          this.submitButton = this.querySelector('[type="submit"]');
          this.buttonText = this.submitButton ? this.submitButton.querySelector('[data-add-text]') : null;
          this.errorEl = this.querySelector('[data-cart-error]');
          this.cartType = this.getAttribute('data-cart-type') || 'notification';
          this.onSubmit = this.onSubmit.bind(this);
          this.form.addEventListener('submit', this.onSubmit);
        }

        disconnectedCallback() {
          if (this.form) this.form.removeEventListener('submit', this.onSubmit);
        }

        onSubmit(evt) {
          evt.preventDefault();
          if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

          this.setError('');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('is-loading');

          var formData = new FormData(this.form);
          fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
            method: 'POST',
            headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          })
            .then(function (response) {
              return response.json().then(function (data) {
                return { ok: response.ok, data: data };
              });
            })
            .then(
              function (result) {
                if (!result.ok || result.data.status) {
                  this.setError(result.data.description || result.data.message || window.fovreStrings.unavailable);
                  return;
                }
                if (this.cartType === 'page') {
                  window.location = (window.routes && window.routes.cart_url) || '/cart';
                  return;
                }
                refreshCartCount();
                this.showAdded();
                document.dispatchEvent(
                  new CustomEvent('fovre:cart:added', { detail: { item: result.data } })
                );
              }.bind(this)
            )
            .catch(
              function () {
                this.setError(window.fovreStrings.unavailable);
              }.bind(this)
            )
            .finally(
              function () {
                this.submitButton.classList.remove('is-loading');
                this.submitButton.removeAttribute('aria-disabled');
              }.bind(this)
            );
        }

        showAdded() {
          if (!this.buttonText) return;
          var original = this.buttonText.getAttribute('data-add-text') || this.buttonText.textContent;
          this.buttonText.setAttribute('data-add-text', original);
          this.buttonText.textContent = window.fovreStrings.added || 'Added';
          setTimeout(
            function () {
              this.buttonText.textContent = original;
            }.bind(this),
            1800
          );
        }

        setError(message) {
          if (!this.errorEl) return;
          this.errorEl.textContent = message;
          this.errorEl.toggleAttribute('hidden', !message);
        }
      }
    );
  }

  /* ---------------------------------------------------------------------------
   * <variant-radios> — updates hidden [name=id], price, and availability when a
   * size/variant option changes. Progressive enhancement over a plain form.
   * ------------------------------------------------------------------------- */
  if ('customElements' in window && !customElements.get('variant-radios')) {
    customElements.define(
      'variant-radios',
      class VariantRadios extends HTMLElement {
        connectedCallback() {
          this.variants = JSON.parse(this.querySelector('[data-variant-json]').textContent);
          this.addEventListener('change', this.onChange.bind(this));
        }

        get selectedOptions() {
          return Array.from(this.querySelectorAll('input[type=radio]:checked')).map(function (i) {
            return i.value;
          });
        }

        onChange() {
          var selected = this.selectedOptions;
          var match = this.variants.find(function (v) {
            return v.options.every(function (opt, i) { return opt === selected[i]; });
          });

          var idInput = this.closest('product-form, .fv-product-form, form').querySelector('[name=id]');
          var priceEl = document.querySelector('[data-product-price]');
          var button = this.closest('product-form, .fv-product-form, form').querySelector('[type=submit]');
          var buttonText = button ? button.querySelector('[data-add-text]') : null;

          if (!match) {
            if (button) button.setAttribute('disabled', 'disabled');
            if (buttonText) buttonText.textContent = window.fovreStrings.unavailable;
            return;
          }

          if (idInput) idInput.value = match.id;
          if (priceEl && typeof match.price === 'number') {
            priceEl.textContent = this.formatMoney(match.price);
          }
          if (button && buttonText) {
            if (match.available) {
              button.removeAttribute('disabled');
              buttonText.textContent = window.fovreStrings.addToCart;
            } else {
              button.setAttribute('disabled', 'disabled');
              buttonText.textContent = window.fovreStrings.soldOut;
            }
          }

          document.dispatchEvent(new CustomEvent('fovre:variant:change', { detail: { variant: match } }));
        }

        formatMoney(cents) {
          var format = this.getAttribute('data-money-format') || '${{amount}}';
          var value = (cents / 100).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          return format.replace(/\{\{\s*amount\s*\}\}/, value);
        }
      }
    );
  }

  /* ---------------------------------------------------------------------------
   * Mobile menu — <menu-drawer> wraps the toggle + panel in the header.
   * Custom element, so the theme editor re-initialises it on section re-render
   * without any hook in init(). Panel is plain markup: CSS owns the visibility
   * at mobile widths, this only toggles the open state and traps Escape.
   * ------------------------------------------------------------------------- */
  if (!customElements.get('menu-drawer')) {
    customElements.define(
      'menu-drawer',
      class MenuDrawer extends HTMLElement {
        connectedCallback() {
          this.toggle = this.querySelector('[data-menu-toggle]');
          this.panel = this.querySelector('[data-menu-panel]');
          if (!this.toggle || !this.panel) return;

          this.onToggle = this.onToggle.bind(this);
          this.onKeydown = this.onKeydown.bind(this);

          this.toggle.addEventListener('click', this.onToggle);
          document.addEventListener('keydown', this.onKeydown);
        }

        disconnectedCallback() {
          document.removeEventListener('keydown', this.onKeydown);
          document.documentElement.classList.remove('fv-menu-open');
        }

        onToggle() {
          this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true');
        }

        onKeydown(event) {
          if (event.key !== 'Escape') return;
          if (this.toggle.getAttribute('aria-expanded') !== 'true') return;
          this.setOpen(false);
          this.toggle.focus();
        }

        setOpen(open) {
          this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          this.panel.toggleAttribute('hidden', !open);
          // Lock body scroll behind the full-screen panel.
          document.documentElement.classList.toggle('fv-menu-open', open);
        }
      }
    );
  }

  /* ---------------------------------------------------------------------------
   * Cart quantity steppers — +/- adjust the paired number input.
   * The cart form's native Update/Checkout submit buttons handle the rest.
   * ------------------------------------------------------------------------- */
  function initCartSteppers(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-cart-row]').forEach(function (row) {
      var input = row.querySelector('[data-qty-input]');
      if (!input) return;
      var down = row.querySelector('[data-qty-down]');
      var up = row.querySelector('[data-qty-up]');
      if (down) down.addEventListener('click', function () {
        var v = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
        input.value = v;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      if (up) up.addEventListener('click', function () {
        input.value = (parseInt(input.value, 10) || 0) + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* --------------------------------------------------------------------------- */
  function init() {
    // fovre-motion.js (the GSAP orchestrator) claims reveal duty by setting
    // window.FovreMotion in its script body, which runs before DOMContentLoaded.
    // No flag means the motion setting is off or a motion script failed to
    // load — either way this observer is the fallback.
    if (!window.FovreMotion) observeReveals(document);
    initCartSteppers(document);
    initHeroVideo();
    initFeaturedProductVideo();
    refreshCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-scan reveals and re-init the video gates when a section
  // is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    if (!window.FovreMotion) observeReveals(event.target);
    initHeroVideo(event.target);
    initFeaturedProductVideo(event.target);
  });
})();
